/**
 * useBookingNotes — Fetch reservation notes for BookingDetailModal
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 5b-2b: Extracted from BookingDetailModal.jsx notes effect/state.
 *
 * Purpose:
 *   Fetch owner-visible notes for a reservation when the modal is open and reservation_id
 *   is set. Clears on reservation change; resets when modal closes. Same endpoint, params,
 *   gating, and loading/error/empty behavior as the original inline effect.
 *
 * Workflow:
 *   1. When isOpen && reservationId: clear previous data/error if any, then fetch if not
 *      already loading and no data/error (same condition as original modal effect).
 *   2. Client-side 8s timeout; AbortController + isActive guard to avoid setState after
 *      unmount or dependency change. AbortError handled silently.
 *   3. When !isOpen: reset state.
 *
 * Assumptions & Invariants:
 *   - getReservationNotesFn(reservationId) returns a Promise; same contract as ownerApi.getReservationNotes.
 *   - Effect deps [isOpen, reservationId] preserve original trigger behavior (notes effect did not use topPanelOpen).
 *   - No new endpoints; no change to call count or timing.
 *
 * Dependencies:
 *   - React (useState, useEffect)
 *   - getReservationNotesFn passed in by caller (ownerApi.getReservationNotes)
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 5b-2b: Extracted from BookingDetailModal.jsx; AbortController + isActive added.
 */

import { useState, useEffect } from 'react';

/**
 * @param {Object} opts
 * @param {boolean} opts.isOpen - Modal open
 * @param {string|number|null|undefined} opts.reservationId - Reservation ID (booking?.reservation_id || booking?.id)
 * @param {function(string|number): Promise<Object>} opts.getReservationNotesFn - e.g. getReservationNotes from ownerApi
 * @returns {{ notesLoading: boolean, notesError: string|null, notes: Array, refreshNotes: function }}
 */
export function useBookingNotes({ isOpen, reservationId, getReservationNotesFn }) {
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();
    let isActive = true;
    let timeoutId = null;

    if (isOpen && reservationId) {
      // Clear state when reservation_id changes (prevent stale content) — same as original
      if (notes.length > 0 || notesError) {
        setNotes([]);
        setNotesError(null);
      }

      // Fetch if not already loading and not already fetched — same condition as original
      if (!notesLoading && !notes.length && !notesError) {
        if (isActive) {
          setNotesLoading(true);
          setNotesError(null);
        }

        timeoutId = setTimeout(() => {
          if (isActive) {
            console.warn('[BookingDetailModal] Notes fetch timeout after 8s');
            setNotesLoading(false);
            setNotesError('Could not load notes (timeout)');
            setNotes([]);
          }
        }, 8000);

        getReservationNotesFn(reservationId)
          .then((data) => {
            clearTimeout(timeoutId);
            if (!isActive) return;

            if (data?.outcome === 'success') {
              const notesArray = data.notes || data.data?.notes || [];
              setNotes(notesArray);
              setNotesError(null);
            } else {
              setNotes([]);
              setNotesError(null);
            }
          })
          .catch((err) => {
            clearTimeout(timeoutId);
            if (!isActive) return;
            if (err?.name === 'AbortError') return;

            console.error('[BookingDetailModal] Error fetching notes:', err);
            // 404 / 403: treat as empty notes (aligned with reviews tab; avoid scary HTTP line when modal loaded)
            if (err?.status === 404 || err?.status === 403) {
              setNotes([]);
              setNotesError(null);
            } else {
              setNotesError(err?.message || 'Could not load notes');
              setNotes([]);
            }
          })
          .finally(() => {
            clearTimeout(timeoutId);
            if (isActive) setNotesLoading(false);
          });
      }
    } else if (!isOpen) {
      if (notesLoading || notes.length > 0 || notesError) {
        setNotes([]);
        setNotesLoading(false);
        setNotesError(null);
      }
    }

    return () => {
      abortController.abort();
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen, reservationId]);

  // No retry in NotesPanel today; return no-op for API parity
  const refreshNotes = () => {};

  return { notesLoading, notesError, notes, refreshNotes };
}
